import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {orderBy} from 'lodash';

import {labels, currentResource} from '../../../../../../util/utils';
import {accessLogOptions, concurrencyErrorCode, inventedConcurrencyErrorCode} from '../../../../../../util/uiconstants';
import {
	ignoreAllPotentialContactsToAdd,
	ignoreAllContactsToRemove,
	addAllPotentialContacts,
	removeAllPotentialContactsToRemove
} from '../../../../../../actions/ContactDBAccessLog';
import {updateDisplayData} from '../../../../../../actions/Metrics/dashboardContextAction';
import {raiseJSValidationError} from '../../../../../../actions/erroractions';

import OutputText from '@ey/canvascoreservices/OutputText';

import Button from '../../../../../CoreWrappers/Button';
import HrAlt from '../../../../../Common/Hr/HrAlt';
import StyledControls from './StyledControls';
import ContactsToAdd from './ContactsToAdd';
import ContactsToRemove from './ContactsToRemove';
import normalizeAddAllContacts from './ContactsToAdd/normalizeAddAllContacts';
import normalizeRemoveAllContacts from './ContactsToRemove/normalizeRemoveAllContacts';
import ConfirmationModal from '../AccessLogContent/ContactsToAdd/ConfirmationModal';
import StyledContent from './StyledContent';
import TabNavigation from '../../../../../CoreWrappers/TabNavigation';
import BorderBox from '../../../../../Common/BorderBox';
import IconMessage from '../../../../../CoreWrappers/IconMessage';

const AccessLogContent = ({setIsLoading}) => {
	const [tab, setTab] = useState(currentResource.accessLogResource[0]);

	const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
	const [missingDomains, setMissingDomains] = useState({});

	const project = useSelector(({project}) => project);
	const contactsToAdd = useSelector(({contactsAccessLog}) => contactsAccessLog.contactsToAdd);
	const contactsToRemove = useSelector(({contactsAccessLog}) => contactsAccessLog.contactsToRemove);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);
	const entities = useSelector(({dashboardContext}) => dashboardContext?.entities);
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);
	const engagementMetadata = useSelector((store) => store?.engagementMetaData);

	const dispatch = useDispatch();

	const displayCustomError = (error, users) => {
		setIsLoading(false);
		const errorCode = error?.response?.data?.errors?.Error[0]?.split('-')[0].trim() || '';
		if (errorCode == concurrencyErrorCode) {
			const resouceLabel = currentResource.Errors[errorCode];
			let names = '';
			users?.forEach(({name, lastName}, i) => {
				if (i === users.length - 1) {
					names = names + name + ' ' + lastName;
				} else {
					names = names + name + ' ' + lastName + ', ';
				}
			});

			dispatch(raiseJSValidationError(inventedConcurrencyErrorCode + ' - ' + resouceLabel.replace('{0}', names)));
		}
	};

	const dispatchAddAllPotentialContacts = () => {
		const apiCalls = [];
		setIsLoading(true);
		let newEntities = [...entities];

		contactsToAdd
			.filter(({contacts}) => contacts?.length > 0)
			.forEach((entity) => {
				apiCalls.push(() =>
					dispatch(addAllPotentialContacts(project?.projectId, entity.contacts, entity.entityId))
						.then(() => {
							newEntities = normalizeAddAllContacts(newEntities, entity.contacts, entity.entityId, engagementMetadata);
							dispatch(updateDisplayData(engagements, newEntities, orgChart));
						})
						.catch((error) => displayCustomError(error, entity.contacts))
				);
			});

		const sequentionallyDispatchApiArray = async () => {
			// This array of API calls needs to be executed sequentionally otherwise it fails (cos' CosmoDB)
			const results = [];
			for (const call of apiCalls) results.push(await Promise.allSettled([call()]));
			return results.flat();
		};

		sequentionallyDispatchApiArray()
			.then(() => {
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const isValidExternDomain = (entityDomains, userDomain) => {
		const missingDomains = entityDomains?.filter((domain) => {
			let domainPattern = domain.replace('@', '\\@');
			domainPattern = domainPattern.replaceAll('*', '\\w+');
			domainPattern = domainPattern.replaceAll('.', '\\.*');

			let regexPattern = new RegExp(domainPattern);

			return !regexPattern.test(userDomain);
		});

		return !(missingDomains?.length === entityDomains?.length);
	};

	const handleAddAllContactsToAdd = () => {
		let missingDomainsPerEntity = [];

		contactsToAdd?.forEach((entity) => {
			if (entity?.contacts.length !== 0) {
				const userDomains = [
					...new Set(
						entity?.contacts
							?.filter((x) => x.isInternal === false)
							?.map(({emailAddress}) => {
								return `@${emailAddress.split('@')[1]}`;
							})
					)
				];

				const entityDomains = project?.orgStructure?.find((x) => x.entityId === entity.entityId)?.domains ?? [];

				const missingEntityDomains = userDomains?.filter((domain) => !isValidExternDomain(entityDomains, domain));

				if (missingEntityDomains.length !== 0) {
					missingDomainsPerEntity.push({
						entityName: entity?.entityName,
						domains: orderBy(missingEntityDomains, (domain) => domain?.toLowerCase())
							?.flat()
							?.join(', ')
					});
				}
			}
		});

		if (missingDomainsPerEntity.length === 0) {
			dispatchAddAllPotentialContacts();
		} else {
			missingDomainsPerEntity = orderBy(missingDomainsPerEntity, (DomainsPerEntity) =>
				DomainsPerEntity?.entityName?.toLowerCase()
			);
			setMissingDomains(missingDomainsPerEntity);
			setOpenConfirmationModal(true);
		}
	};

	const handleIgnoreAllContactsToAdd = () => {
		const apiCalls = [];
		setIsLoading(true);
		contactsToAdd
			.filter(({contacts}) => contacts?.length > 0)
			.forEach((entity) => {
				apiCalls.push(() =>
					dispatch(
						ignoreAllPotentialContactsToAdd(project?.projectId, entity.contacts, entity.entityId)
					).catch((error) => displayCustomError(error, entity.contacts))
				);
			});

		const sequentionallyDispatchApiArray = async () => {
			// This array of API calls needs to be executed sequentionally otherwise it fails (cos' CosmoDB)
			const results = [];
			for (const call of apiCalls) results.push(await Promise.allSettled([call()]));
			return results.flat();
		};

		sequentionallyDispatchApiArray()
			.then(() => {
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const hasContacts = (contactsToRemove) => {
		return contactsToRemove?.some((item) => item?.contacts && item?.contacts?.length > 0);
	};

	const handleIgnoreAllContactsToRemove = () => {
		if (hasContacts(contactsToRemove)) {
			const apiCalls = [];
			setIsLoading(true);
			contactsToRemove
				.filter(({contacts}) => contacts?.length > 0)
				.forEach((entity) => {
					apiCalls.push(
						Promise.resolve(dispatch(ignoreAllContactsToRemove(project?.projectId, entity.contacts, entity.entityId)))
					);
					Promise.all(apiCalls)
						.then(() => setIsLoading(false))
						.catch((error) => displayCustomError(error, entity.contacts));
				});
		}
	};

	const handleRemoveAllPotentialContactsToRemove = () => {
		if (hasContacts(contactsToRemove)) {
			const apiCalls = [];
			setIsLoading(true);
			let newEntities = [...entities];
			contactsToRemove
				.filter(({contacts}) => contacts?.length > 0)
				.forEach((entity, index) => {
					apiCalls.push(
						Promise.resolve(
							dispatch(removeAllPotentialContactsToRemove(project?.projectId, entity.contacts, entity.entityId))
						)
					);
					Promise.all(apiCalls)
						.then(() => {
							newEntities = normalizeRemoveAllContacts(newEntities, entity.contacts, entity.entityId);
							dispatch(updateDisplayData(engagements, newEntities, orgChart));
							setIsLoading(false);
						})
						.catch((error) => displayCustomError(error, entity.contacts));
				});
		}
	};

	const renderInstructional = () => <OutputText light>{labels.accessLogInstructional}</OutputText>;

	const renderBulkControl = (props) => <Button {...props} />;

	const renderAddControls = () => (
		<>
			{renderBulkControl({
				onClick: handleAddAllContactsToAdd,
				variant: 'secondary',
				buttonTitle: labels.addAll,
				label: labels.addAll
			})}
			{renderBulkControl({
				onClick: handleIgnoreAllContactsToAdd,
				variant: 'ghost',
				buttonTitle: labels.ignoreAll,
				label: labels.ignoreAll
			})}
		</>
	);

	const renderRemoveControls = () => (
		<>
			{renderBulkControl({
				onClick: handleRemoveAllPotentialContactsToRemove,
				variant: 'secondary',
				buttonTitle: labels.removeAll,
				label: labels.removeAll
			})}
			{renderBulkControl({
				onClick: handleIgnoreAllContactsToRemove,
				variant: 'ghost',
				buttonTitle: labels.ignoreAll,
				label: labels.ignoreAll
			})}
		</>
	);

	const renderControls = () => {
		const props = {
			tabs: currentResource.accessLogResource,
			activeTab: tab,
			setTab
		};

		return (
			<StyledControls>
				<TabNavigation {...props} />
				{tab.id === accessLogOptions.contactsToAdd && renderAddControls()}
				{tab.id === accessLogOptions.contactsToRemove && renderRemoveControls()}
			</StyledControls>
		);
	};

	const renderTabs = () => {
		const props = {
			setIsLoading,
			renderEmpty: (text) => (
				<BorderBox>
					<IconMessage subTitle={text} title={null} addSpacing />
				</BorderBox>
			)
		};

		return (
			<>
				{tab.id === accessLogOptions.contactsToAdd && <ContactsToAdd {...props} />}
				{tab.id === accessLogOptions.contactsToRemove && <ContactsToRemove {...props} />}
			</>
		);
	};

	const renderConfirmationModal = () => {
		const props = {
			missingDomains,
			contactsToAdd,
			projectId: project?.projectId,
			entities,
			engagementMetadata,
			engagements,
			orgChart,
			isAddAll: true,
			message: labels.addAllConfirmationModal
		};

		return <ConfirmationModal {...props} />;
	};

	return (
		<StyledContent>
			{renderInstructional()}
			<HrAlt />
			{renderControls()}
			{renderTabs()}
			{openConfirmationModal && renderConfirmationModal()}
		</StyledContent>
	);
};

AccessLogContent.propTypes = {};

AccessLogContent.defaultProps = {};

export default AccessLogContent;
